// services/SSEService.js
import axios from 'axios';

export class SSEService {
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
    this.eventSource = null;
    this.sessionId = null;
    this.listeners = new Map();
  }
  
  /**
   * Get a new session ID from the server
   */
  async createSessionId() {
    try {
      console.log('🌐 Fetching SSE session from:', `${this.baseUrl}/api/hmi/sse/assignment-session`);
      const response = await axios.get(`${this.baseUrl}/api/hmi/sse/assignment-session`);
      console.log('🌐 SSE session response:', response.data);
      this.sessionId = response.data.sessionId;
      return this.sessionId;
    } catch (error) {
      console.error('🌐 Failed to get SSE session ID:', error);
      throw error;
    }
  }
  
  /**
   * Connect to SSE stream
   */
  async connect() {
    if (!this.sessionId) {
      await this.createSessionId();
    }
    
    // Close existing connection if any
    this.disconnect();
    
    // Create new EventSource connection
    const sseUrl = `${this.baseUrl}/api/hmi/sse/assignment-updates/${this.sessionId}`;
    console.log('🌐 Connecting to SSE:', sseUrl);
    this.eventSource = new EventSource(sseUrl);
    
    // Handle messages
    this.eventSource.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (error) {
        console.error('Failed to parse SSE message:', error);
      }
    });
    
    // Handle errors
    this.eventSource.addEventListener('error', (error) => {
      console.error('SSE connection error:', error);
      this.notifyListeners('error', { error: 'Connection lost' });
      
      // Auto-reconnect after 5 seconds
      setTimeout(() => {
        if (this.eventSource?.readyState === EventSource.CLOSED) {
          console.log('Attempting to reconnect SSE...');
          this.connect();
        }
      }, 5000);
    });
    
    // Handle open
    this.eventSource.addEventListener('open', () => {
      console.log('SSE connection opened');
      this.notifyListeners('connected', { sessionId: this.sessionId });
    });
    
    return this.sessionId;
  }
  
  /**
   * Disconnect from SSE stream
   */
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
  
  /**
   * Handle incoming messages
   */
  handleMessage(data) {
    switch (data.type) {
      case 'assignment-progress':
        this.notifyListeners('progress', data);
        break;
        
      case 'assignment-complete':
        this.notifyListeners('complete', data);
        // Auto-disconnect after completion
        setTimeout(() => this.disconnect(), 1000);
        break;
        
      case 'error':
        this.notifyListeners('error', data);
        this.disconnect();
        break;
        
      default:
        console.warn('Unknown SSE message type:', data.type);
    }
  }
  
  /**
   * Register event listener
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    
    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }
  
  /**
   * Remove event listener
   */
  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }
  
  /**
   * Notify all listeners for an event
   */
  notifyListeners(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in SSE listener for event ${event}:`, error);
        }
      });
    }
  }
  
  /**
   * Get current session ID
   */
  getSessionId() {
    return this.sessionId;
  }
  
  /**
   * Check if connected
   */
  isConnected() {
    return this.eventSource?.readyState === EventSource.OPEN;
  }
  
  /**
   * Clean up
   */
  cleanup() {
    this.disconnect();
    this.listeners.clear();
    this.sessionId = null;
  }
}